import React, { useState, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { DetailsModal } from "components/grid/DetailsModal";
import { FormatToCurrency } from "shared/helpers/MoneyHelpers";
import { DateTime } from "luxon";
import KendoDropdown from "../../../components/form/KendoDropdown";
import { TextArea } from "@progress/kendo-react-inputs";
import { useFetchMissingRemittanceDetails } from "./hooks/useFetchMissingRemittanceDetails";
import { UserContext } from "shared/context/UserContext";
import { MissingRemittanceStatusConstants } from "./constants/MissingRemittanceStatusConstants";
import { useInsertNoteHistory } from "./hooks/useInsertNoteHistory";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";
import { ControlSpinner } from "shared/components/spinners/ControlSpinner";

export default function AdminMissingRemittanceDetailsModal(props) {
  const {
    showModal,
    setShowModal,
    parentComponentRef,
    missingRemittanceRequest,
  } = props;
  const { invalidateReactQueryKey } = useReactQueryHelpers();

  let { email: userEmail } = useContext(UserContext);
  let detailsProp = {
    mpid: missingRemittanceRequest.mpid,
    user: userEmail,
  };
  const { data: missingRemittanceHistoryData } =
    useFetchMissingRemittanceDetails(
      detailsProp,
      missingRemittanceRequest !== null
    );
  let { mutate: mutateForInsertNoteHistory } = useInsertNoteHistory();

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [userEnteredNote, setUserEnteredNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  function handleSubmit(e) {
    if (!selectedStatus) {
      alert("Please select a status for this update.");
      return;
    }

    let statusFlag = "";

    if (
      missingRemittanceHistoryData &&
      missingRemittanceHistoryData.length > 0
    ) {
      const latestEntry = missingRemittanceHistoryData.reduce(
        (latest, current) => {
          return DateTime.fromISO(current.modifiedDateTime) >
            DateTime.fromISO(latest.modifiedDateTime)
            ? current
            : latest;
        },
        missingRemittanceHistoryData[0]
      );

      let lastNoteStatus = latestEntry.status;
      let newNoteStatus = selectedStatus.label;

      let completedStatuses = ["Manually Received", "Automatically Received"];

      /*
            NOTE: If this request had been closed (meaning the check had been marked as received)
            but now for whatever reason we need to re-open the request, then we need to mark the statusFlag as 1.
            */
      if (
        completedStatuses.includes(lastNoteStatus) &&
        !completedStatuses.includes(newNoteStatus)
      ) {
        statusFlag = "1";
      }
      let mutateParameter = {
        user: userEmail,
        mpid: missingRemittanceRequest.mpid,
        stid: selectedStatus.value,
        statusFlag: statusFlag,
        note: userEnteredNote,
        callback: () => {
          invalidateReactQueryKey(
            "api/MissingRemittances/GetAdminMissingRemittanceNotes"
          );
          setIsLoading(false);
          // Reset form values after successful save
          setUserEnteredNote("");
          setSelectedStatus(null);
        },
      };

      mutateForInsertNoteHistory(mutateParameter);
      setIsLoading(true);
    }
  }

  function resetValuesAndCloseModal() {
    setShowModal(false);
    parentComponentRef.current.focus();
    setUserEnteredNote("");
    setSelectedStatus(null);
  }

  return (
    <DetailsModal show={showModal} handleClose={resetValuesAndCloseModal}>
      <h2>Missing Remittance Detail</h2>
      {missingRemittanceRequest ? (
        <div>
          <Row>
            <Col>NCPDP: {missingRemittanceRequest.ncpdp}</Col>
            <Col>Payer: {missingRemittanceRequest.payer}</Col>
          </Row>
          <Row>
            <Col>Check Number: {missingRemittanceRequest.checkNumber}</Col>
            <Col>
              Central Pay: {missingRemittanceRequest.centralPay ? "Yes" : "No"}
            </Col>
          </Row>
          <Row>
            <Col>
              Check Date:{" "}
              {DateTime.fromJSDate(missingRemittanceRequest.checkDate).toFormat(
                "MM/dd/yyyy"
              )}
            </Col>
            <Col>
              Check Amount:{" "}
              {FormatToCurrency(missingRemittanceRequest.checkAmount)}
            </Col>
          </Row>
          <hr />
          <Row>
            <Col className="col-sm-6">
              <div>Status:</div>
              <KendoDropdown
                dataSource={MissingRemittanceStatusConstants}
                textField="label"
                idField="value"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e);
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <div>Notes:</div>
              <div>
                <TextArea
                  rows={10}
                  value={userEnteredNote}
                  onChange={(e) => {
                    setUserEnteredNote(e.target.value);
                  }}
                />
              </div>
              <br />
              <div>
                <Button
                  className="inline-show-result-button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </Col>
            <Col>
              <div>Notes History:</div>
              {!isLoading && missingRemittanceHistoryData ? (
                missingRemittanceHistoryData.map((item, index) => (
                  <div key={"missingRemittanceHistoryData" + index}>
                    <div>{item.modifiedUser}</div>
                    <div>{item.status}</div>
                    {item.note ? <div>{item.note}</div> : null}
                    {item.modifiedDateTime ? (
                      <div>
                        {DateTime.fromISO(item.modifiedDateTime).toFormat(
                          "MM/dd/yyyy"
                        )}
                      </div>
                    ) : null}
                    <br />
                  </div>
                ))
              ) : (
                <ControlSpinner />
              )}
            </Col>
          </Row>
          <br />
        </div>
      ) : null}
    </DetailsModal>
  );
}
